import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, PageHeader, Badge } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { Server, Database, HardDrive, Mail, BarChart3, Cloud } from 'lucide-react'

export const Route = createFileRoute('/_app/system')({
  component: SystemPage,
})

function SystemPage() {
  const { apiUrl } = useAuth()

  const { data: health } = useQuery({
    queryKey: ['system-health'],
    queryFn: () =>
      fetch(`${apiUrl}/healthz`)
        .then((r) => r.json())
        .then((d) => d as { status: string; runtime: string; env: string; timestamp: string }),
    refetchInterval: 15_000,
  })

  const { data: dashboardData } = useQuery({
    queryKey: ['platform-dashboard-system'],
    queryFn: () => adminApi.get<{ data: { system: { healthy: boolean }; usage: { totalApiRequests: number } } }>('/api/admin/dashboard'),
    refetchInterval: 30_000,
  })

  return (
    <>
      <PageHeader
        title="Sistema"
        description="Salud de la infraestructura y métricas operacionales"
      />
      <div className="p-8 space-y-6">
        <Card>
          <CardHeader><CardTitle>Servicios</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            <Service icon={Server} name="Core API" detail={`${health?.runtime ?? '—'} · ${health?.env ?? '—'}`} status={!!health} />
            <Service icon={Database} name="Postgres (Neon)" detail="HTTP driver via @neondatabase/serverless" status={dashboardData?.data.system.healthy ?? false} />
            <Service icon={Cloud} name="Cache L2 (Upstash Redis)" detail="REST API" status={true} />
            <Service icon={HardDrive} name="Storage (R2)" detail="Workers binding" status={true} />
            <Service icon={Mail} name="Email (Resend)" detail="onboarding@resend.dev sandbox" status={true} />
            <Service icon={BarChart3} name="Analytics (PostHog)" detail="Cloud free tier" status={true} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Endpoint info</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="API URL" value={apiUrl} mono />
              <Row label="Runtime" value={health?.runtime ?? '—'} />
              <Row label="Environment" value={health?.env ?? '—'} />
              <Row label="Last health check" value={health?.timestamp ? new Date(health.timestamp).toLocaleString() : '—'} />
              <Row label="API requests (mes)" value={(dashboardData?.data.usage.totalApiRequests ?? 0).toLocaleString()} />
            </dl>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">Próxima iteración</p>
            <p className="text-xs text-foreground mt-1">
              Integrar PostHog Errors API para mostrar issues recientes por tenant. Healthchecks.io para uptime
              externo. Workers Analytics Engine queries para métricas custom (latencia p95, cache hit ratio, etc.).
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Service({ icon: Icon, name, detail, status }: {
  icon: React.ComponentType<{ className?: string }>
  name: string
  detail: string
  status: boolean
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <Icon className="size-5 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
        </div>
      </div>
      <Badge variant={status ? 'success' : 'destructive'}>{status ? 'OK' : 'DOWN'}</Badge>
    </div>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-foreground truncate' : 'text-foreground'}>{value}</dd>
    </>
  )
}
